import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { reloadOnceOnChunkError } from "@/lib/chunk-reload";
import { loadAIChatModule } from "@/components/lazyAIChatLoad";
import { FeatureErrorBoundary } from "@/components/FeatureErrorBoundary";

const LazyAIChat = lazy(async () => {
  try {
    const mod = await loadAIChatModule();
    const Component = mod?.AIChat;
    if (!Component) {
      throw new Error("AIChat export not found in module");
    }
    return { default: Component };
  } catch (error: unknown) {
    reloadOnceOnChunkError(error);
    throw error;
  }
});

interface LazyAIChatPanelProps {
  onClose: () => void;
}

/** Lazy AI chat with error boundary — use instead of bare Suspense + LazyAIChat. */
export function LazyAIChatPanel({ onClose }: LazyAIChatPanelProps) {
  const { t } = useTranslation();

  return (
    <FeatureErrorBoundary
      scope="ai-chat"
      titleKey="errors.feature.aiChat.title"
      bodyKey="errors.feature.aiChat.body"
      errorContainerClassName="ai-chat-panel"
      errorContainerRole="dialog"
      errorContainerAriaLabel={t("aiChat.panelAria")}
      extraAction={
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          {t("aiChat.errorBoundary.close")}
        </button>
      }
    >
      <Suspense fallback={null}>
        <LazyAIChat onClose={onClose} />
      </Suspense>
    </FeatureErrorBoundary>
  );
}
